import React, { useContext, useEffect, useLayoutEffect, useMemo, useReducer, useRef } from "react";
import { makeGet } from "@culturehq/client";

import { Profile, Story } from "chq";
import { filtersToQuery, queryToOptions } from "components/admin/settings/EmbedStoriesSlider";
import { DefaultProfile, useProfile } from "./ProfileContext";

const StoriesStackContext = React.createContext<ContextValue | undefined>(undefined);

interface Pagination {
  currentPage?: number;
  totalPages?: number;
}

interface State {
  fetching: boolean;
  appending: number | null;
  error: any;
  pagination: Pagination;
  activeStory?: Story;
  stories: Story[];
  selectedStories: Story[];
  selectedFilters: any[];
  columnWidth: string;
}

interface ContextValue {
  state: State;
  isControlledMode: boolean;
  isAppendEnable: boolean;
  onSelectFilters: (selectedFilters: any[]) => void;
  onActiveStory: (activeStory: Story) => void;
  onSelectStories: (selectedStories: Story[], options?: { append?: boolean }) => void;
  onAppendStart: () => void;
  onReset: () => void;
}

const initialState: State = {
  fetching: false,
  appending: null,
  error: null,
  pagination: { currentPage: 0, totalPages: 0 },
  activeStory: undefined,
  stories: [],
  selectedStories: [],
  selectedFilters: [],
  columnWidth: "50%"
};

type Action =
  | { type: "appendStart" }
  | { type: "appendSuccess"; response: any }
  | { type: "appendError"; error: any }
  | { type: "fetchStart" }
  | { type: "fetchSuccess"; response: any; profile: any }
  | { type: "fetchError"; error: any }
  | { type: "updateSelectedStories"; selectedStories: Story[]; append?: boolean }
  | { type: "updateSelectedFilters"; selectedFilters: any[] }
  | { type: "updateControlledStories"; controlledStories: Story[] }
  | { type: "updateColumnWidth"; columnWidth: string }
  | { type: "updateActivedStory"; activeStory: Story }
  | { type: "reset" };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "appendStart":
      return { ...state, appending: (state.pagination.currentPage ?? 0) + 1 };
    case "appendSuccess":
      return {
        ...state,
        appending: null,
        pagination: action.response.pagination,
        stories: [...state.stories, ...action.response.stories]
      };
    case "appendError":
      return { ...state, appending: null, error: action.error };
    case "fetchStart":
      return { ...state, fetching: true, error: null };
    case "fetchSuccess":
      return {
        ...state,
        fetching: false,
        error: null,
        pagination: action.response.pagination,
        stories: action.response.stories
      };
    case "fetchError":
      return { ...state, fetching: false, error: action.error };
    case "updateSelectedStories":
      return {
        ...state,
        selectedStories: action.append
          ? [...state.selectedStories, ...action.selectedStories]
          : action.selectedStories
      };
    case "updateSelectedFilters":
      return { ...state, selectedFilters: action.selectedFilters };
    case "updateControlledStories":
      return { ...state, stories: action.controlledStories };
    case "updateColumnWidth":
      return { ...state, columnWidth: action.columnWidth };
    case "updateActivedStory":
      return { ...state, activeStory: action.activeStory };
    case "reset":
      return initialState;
    default:
      return state;
  }
};

const useFetchStories = (
  isControlledMode: boolean,
  dispatch: React.Dispatch<Action>,
  profile: Profile | DefaultProfile,
  isOpen: boolean,
  { selectedFilters }: State
) => useEffect(() => {
  if (!isOpen) return;
  if (isControlledMode) return;

  let cancelled = false;
  dispatch({ type: "fetchStart" });

  const query = filtersToQuery(selectedFilters);
  makeGet("/stories", { ...queryToOptions(query), page: 1 })
    .then(response => {
      if (cancelled) return;
      dispatch({ type: "fetchSuccess", response, profile });
    })
    .catch(error => {
      if (cancelled) return;
      dispatch({ type: "fetchError", error });
    });

  // eslint-disable-next-line consistent-return
  return () => {
    cancelled = true;
  };
}, [isControlledMode, dispatch, profile, isOpen, selectedFilters]);

const useAppendStories = (
  isControlledMode: boolean,
  dispatch: React.Dispatch<Action>,
  { appending, selectedFilters }: State
) => useEffect(() => {
  if (isControlledMode) return;
  if (!appending) return;

  let cancelled = false;

  const query = filtersToQuery(selectedFilters);
  makeGet("/stories", { ...queryToOptions(query), page: appending })
    .then(response => {
      if (cancelled) return;
      dispatch({ type: "appendSuccess", response });
    })
    .catch(error => {
      if (cancelled) return;
      dispatch({ type: "appendError", error });
    });

  // eslint-disable-next-line consistent-return
  return () => {
    cancelled = true;
  };
}, [isControlledMode, dispatch, appending, selectedFilters]);

const useControlledStories = (
  isControlledMode: boolean,
  dispatch: React.Dispatch<Action>,
  controlledStories: Story[],
  isOpen: boolean
) => useEffect(() => {
  if (!isControlledMode || !isOpen) return;
  dispatch({ type: "updateControlledStories", controlledStories });
}, [isControlledMode, dispatch, controlledStories, isOpen]);

const getColumnWidth = (storiesNumber: number) => {
  if (window.innerWidth >= 768 && storiesNumber !== 1) {
    return "50%";
  }
  return "100%";
};

const useColumnWidthListen = (
  dispatch: React.Dispatch<Action>, { stories }: State
) => useLayoutEffect(() => {
  const updateSize = () => dispatch({ type: "updateColumnWidth", columnWidth: getColumnWidth(stories.length) });

  window.addEventListener("resize", updateSize);
  return () => window.removeEventListener("resize", updateSize);
}, [dispatch, stories]);

interface StoriesStackProviderProps {
  isOpen: boolean;
  children: React.ReactNode;
  controlledStories?: Story[];
}

const StoriesStackProvider: React.FC<StoriesStackProviderProps> = ({
  isOpen, children, controlledStories = []
}) => {
  const { profile } = useProfile();
  const [state, dispatch] = useReducer(reducer, initialState);
  const isControlledMode = !!controlledStories.length;
  const isAppendEnable = !isControlledMode && !state.appending
    && (state.pagination?.currentPage || 0) < (state.pagination?.totalPages || 0);

  useFetchStories(isControlledMode, dispatch, profile, isOpen, state);
  useAppendStories(isControlledMode, dispatch, state);
  useControlledStories(isControlledMode, dispatch, controlledStories, isOpen);
  useColumnWidthListen(dispatch, state);

  const onSelectFilters = (selectedFilters: any[]) => dispatch({ type: "updateSelectedFilters", selectedFilters });
  const onActiveStory = (activeStory: Story) => dispatch({ type: "updateActivedStory", activeStory });
  const onSelectStories = (selectedStories: Story[], { append }: { append?: boolean } = {}) => dispatch({ type: "updateSelectedStories", selectedStories, append });
  const onAppendStart = () => dispatch({ type: "appendStart" });
  const onReset = () => dispatch({ type: "reset" });

  useEffect(() => {
    if (!isOpen) onReset();
  }, [isOpen]);

  const value: ContextValue = useMemo(() => ({
    state,
    isControlledMode,
    isAppendEnable,
    onSelectFilters,
    onActiveStory,
    onSelectStories,
    onAppendStart,
    onReset
  }), [state, isControlledMode, isAppendEnable]);

  return (
    <StoriesStackContext.Provider value={value}>
      {children}
    </StoriesStackContext.Provider>
  );
};

const useStoriesStack = (): ContextValue => {
  const context = useContext(StoriesStackContext);
  if (!context) {
    throw new Error("useStoriesStack must be used within a StoriesStackProvider");
  }
  return context;
};

export {
  StoriesStackProvider,
  useStoriesStack
};
